import React, { useState } from 'react';
import { 
  Plus, 
  Clock, 
  MapPin, 
  User, 
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  BookOpen,
  Calendar as CalendarIcon,
  AlertCircle,
  CheckCircle2,
  Save,
  X,
  Eye
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

interface Subject {
  id: string;
  name: string;
  professor: string;
  classroom: string;
  color: string;
  lastGrade: number;
  schedule: {
    day: number; // 0-6 (Sunday-Saturday)
    startTime: string;
    endTime: string;
  }[];
  description?: string;
  credits?: number;
  semester?: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  subjectId: string;
  dueDate: string;
  deliveryLocation: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in-progress' | 'completed';
  createdAt: Date;
}

interface ScheduleSlot {
  day: number;
  startTime: string;
  endTime: string;
}

const AcademicCalendar: React.FC = () => {
  const { colors } = useTheme();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showSubjectDetails, setShowSubjectDetails] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const [subjects, setSubjects] = useState<Subject[]>([
    {
      id: '1',
      name: 'Matemáticas Avanzadas',
      professor: 'Dr. García',
      classroom: 'A-101',
      color: colors.blue,
      lastGrade: 8.5,
      description: 'Cálculo diferencial e integral aplicado',
      credits: 4,
      semester: '2025-1',
      schedule: [
        { day: 1, startTime: '08:00', endTime: '10:00' },
        { day: 3, startTime: '10:00', endTime: '12:00' }
      ]
    },
    {
      id: '2',
      name: 'Programación Web',
      professor: 'Ing. López',
      classroom: 'B-205',
      color: colors.green,
      lastGrade: 9.2,
      description: 'Desarrollo frontend y backend con tecnologías modernas',
      credits: 5,
      semester: '2025-1',
      schedule: [
        { day: 2, startTime: '14:00', endTime: '16:00' },
        { day: 4, startTime: '08:00', endTime: '10:00' }
      ]
    },
    {
      id: '3',
      name: 'Base de Datos',
      professor: 'Dra. Martínez',
      classroom: 'C-301',
      color: colors.mauve,
      lastGrade: 7.8,
      description: 'Diseño y administración de bases de datos relacionales',
      credits: 4,
      semester: '2025-1',
      schedule: [
        { day: 1, startTime: '16:00', endTime: '18:00' },
        { day: 5, startTime: '10:00', endTime: '12:00' }
      ]
    }
  ]);

  const [tasks, setTasks] = useState<Task[]>([
    {
      id: '1',
      title: 'Proyecto Final React',
      description: 'Desarrollar aplicación web completa con React y Node.js',
      subjectId: '2',
      dueDate: '2025-02-15',
      deliveryLocation: 'Plataforma Virtual',
      priority: 'high',
      status: 'in-progress',
      createdAt: new Date('2025-01-10')
    },
    {
      id: '2',
      title: 'Examen Parcial',
      description: 'Examen sobre cálculo diferencial e integral',
      subjectId: '1',
      dueDate: '2025-01-25',
      deliveryLocation: 'Aula A-101',
      priority: 'urgent',
      status: 'pending',
      createdAt: new Date('2025-01-15')
    },
    {
      id: '3',
      title: 'Diseño de BD',
      description: 'Crear modelo entidad-relación para sistema de biblioteca',
      subjectId: '3',
      dueDate: '2025-02-01',
      deliveryLocation: 'Oficina Profesora',
      priority: 'medium',
      status: 'pending',
      createdAt: new Date('2025-01-12')
    }
  ]);

  const [newSubject, setNewSubject] = useState<Partial<Subject>>({
    name: '',
    professor: '',
    classroom: '',
    color: colors.blue,
    lastGrade: 0,
    description: '',
    credits: 3,
    semester: '2025-1',
    schedule: []
  });

  const [newTask, setNewTask] = useState<Partial<Task>>({
    title: '',
    description: '',
    subjectId: '',
    dueDate: '',
    deliveryLocation: '',
    priority: 'medium',
    status: 'pending'
  });

  const [tempSchedule, setTempSchedule] = useState<ScheduleSlot[]>([]);

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const timeSlots = Array.from({ length: 14 }, (_, i) => {
    const hour = 7 + i;
    return `${hour.toString().padStart(2, '0')}:00`;
  });

  const availableColors = [
    colors.blue, colors.green, colors.mauve, colors.red, 
    colors.yellow, colors.pink, colors.teal, colors.peach
  ];

  const getWeekDays = (date: Date) => {
    const week = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDays = getWeekDays(currentWeek);

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeek);
    newDate.setDate(currentWeek.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newDate);
  };

  const getClassesForDay = (dayIndex: number) => {
    return subjects.filter(subject => 
      subject.schedule.some(schedule => schedule.day === dayIndex)
    ).map(subject => ({
      ...subject,
      schedule: subject.schedule.find(s => s.day === dayIndex)!
    }));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return colors.red;
      case 'high': return colors.peach;
      case 'medium': return colors.yellow;
      case 'low': return colors.green;
      default: return colors.blue;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return colors.green;
      case 'in-progress': return colors.blue;
      case 'pending': return colors.yellow;
      default: return colors.subtext0;
    }
  };

  const handleSaveSubject = () => {
    if (newSubject.name && newSubject.professor && newSubject.classroom) {
      const subject: Subject = {
        id: editingSubject?.id || Date.now().toString(),
        name: newSubject.name,
        professor: newSubject.professor,
        classroom: newSubject.classroom,
        color: newSubject.color || colors.blue,
        lastGrade: newSubject.lastGrade || 0,
        description: newSubject.description || '',
        credits: newSubject.credits || 3,
        semester: newSubject.semester || '2025-1',
        schedule: Array.isArray(tempSchedule) ? tempSchedule : []
      };
      
      if (editingSubject) {
        setSubjects(prev => prev.map(s => s.id === editingSubject.id ? subject : s));
      } else {
        setSubjects(prev => [...prev, subject]);
      }
      
      resetSubjectForm();
    }
  };

  const resetSubjectForm = () => {
    setNewSubject({
      name: '',
      professor: '',
      classroom: '',
      color: colors.blue,
      lastGrade: 0,
      description: '',
      credits: 3,
      semester: '2025-1',
      schedule: []
    });
    setTempSchedule([]);
    setEditingSubject(null);
    setShowSubjectModal(false);
  };

  const handleEditSubject = (subject: Subject) => {
    setEditingSubject(subject);
    setNewSubject({
      name: subject.name,
      professor: subject.professor,
      classroom: subject.classroom,
      color: subject.color,
      lastGrade: subject.lastGrade,
      description: subject.description,
      credits: subject.credits,
      semester: subject.semester
    });
    setTempSchedule(Array.isArray(subject.schedule) ? [...subject.schedule] : []);
    setShowSubjectModal(true);
  };

  const handleDeleteSubject = (subjectId: string) => {
    if (confirm('¿Estás seguro de que quieres eliminar esta materia? También se eliminarán todas las tareas asociadas.')) {
      setSubjects(prev => prev.filter(s => s.id !== subjectId));
      setTasks(prev => prev.filter(t => t.subjectId !== subjectId));
    }
  };

  const handleViewSubject = (subject: Subject) => {
    setSelectedSubject(subject);
    setShowSubjectDetails(true);
  };

  const addScheduleSlot = () => {
    setTempSchedule(prev => [...(Array.isArray(prev) ? prev : []), { day: 1, startTime: '08:00', endTime: '10:00' }]);
  };

  const updateScheduleSlot = (index: number, field: keyof ScheduleSlot, value: string | number) => {
    setTempSchedule(prev => {
      if (!Array.isArray(prev)) return [];
      return prev.map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      );
    });
  };

  const removeScheduleSlot = (index: number) => {
    setTempSchedule(prev => {
      if (!Array.isArray(prev)) return [];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSaveTask = () => {
    if (newTask.title && newTask.subjectId && newTask.dueDate) {
      const task: Task = {
        id: Date.now().toString(),
        title: newTask.title,
        description: newTask.description || '',
        subjectId: newTask.subjectId,
        dueDate: newTask.dueDate,
        deliveryLocation: newTask.deliveryLocation || '',
        priority: newTask.priority || 'medium',
        status: newTask.status || 'pending',
        createdAt: new Date()
      };
      
      setTasks(prev => [...prev, task]);
      setNewTask({
        title: '',
        description: '',
        subjectId: '',
        dueDate: '',
        deliveryLocation: '',
        priority: 'medium',
        status: 'pending'
      });
      setShowTaskModal(false);
    }
  };

  const getSubjectById = (id: string) => subjects.find(s => s.id === id);

  const getTasksForSubject = (subjectId: string) => 
    tasks.filter(task => task.subjectId === subjectId);

  const isTimeSlotOccupied = (day: number, startTime: string, endTime: string, excludeSubjectId?: string) => {
    return subjects.some(subject => {
      if (excludeSubjectId && subject.id === excludeSubjectId) return false;
      
      return subject.schedule.some(schedule => {
        if (schedule.day !== day) return false;
        
        const newStart = parseInt(startTime.split(':')[0]);
        const newEnd = parseInt(endTime.split(':')[0]);
        const existingStart = parseInt(schedule.startTime.split(':')[0]);
        const existingEnd = parseInt(schedule.endTime.split(':')[0]);
        
        return (newStart < existingEnd && newEnd > existingStart);
      });
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
          Calendario Académico
        </h1>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowSubjectModal(true)}
            className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: colors.blue }}
          >
            <Plus className="w-4 h-4" />
            Nueva Materia
          </button>
          
          <button 
            onClick={() => setShowTaskModal(true)}
            className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: colors.green }}
          >
            <Plus className="w-4 h-4" />
            Nueva Tarea
          </button>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigateWeek('prev')}
          className="p-2 rounded-lg border transition-colors hover:opacity-80"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <ChevronLeft className="w-4 h-4" style={{ color: colors.text }} />
        </button>
        
        <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
          Semana del {weekDays[0].toLocaleDateString()} - {weekDays[6].toLocaleDateString()}
        </h2>
        
        <button
          onClick={() => navigateWeek('next')}
          className="p-2 rounded-lg border transition-colors hover:opacity-80"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <ChevronRight className="w-4 h-4" style={{ color: colors.text }} />
        </button>
      </div>

      {/* Calendar Grid */}
      <div 
        className="rounded-xl border overflow-hidden"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        {/* Header Row */}
        <div className="grid grid-cols-8 border-b" style={{ borderColor: colors.surface1 }}>
          <div 
            className="p-4 text-center font-medium"
            style={{ 
              backgroundColor: colors.surface0,
              color: colors.text
            }}
          >
            Hora
          </div>
          {weekDays.map((day, index) => (
            <div
              key={index}
              className="p-4 text-center font-medium"
              style={{ 
                backgroundColor: colors.surface0,
                color: colors.text
              }}
            >
              <div>{dayNames[index]}</div>
              <div className="text-sm opacity-70">{day.getDate()}</div>
            </div>
          ))}
        </div>

        {/* Time Slots */}
        {timeSlots.map((time, timeIndex) => (
          <div key={time} className="grid grid-cols-8 border-b" style={{ borderColor: colors.surface1 }}>
            <div 
              className="p-2 text-center text-sm font-medium border-r"
              style={{ 
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.subtext0
              }}
            >
              {time}
            </div>
            {weekDays.map((day, dayIndex) => {
              const classes = getClassesForDay(dayIndex).filter(cls => {
                const startHour = parseInt(cls.schedule.startTime.split(':')[0]);
                const currentHour = 7 + timeIndex;
                const endHour = parseInt(cls.schedule.endTime.split(':')[0]);
                return currentHour >= startHour && currentHour < endHour;
              });

              return (
                <div
                  key={dayIndex}
                  className="p-2 border-r min-h-[60px] relative"
                  style={{ 
                    backgroundColor: colors.base,
                    borderColor: colors.surface1
                  }}
                >
                  {classes.map((cls) => (
                    <div
                      key={cls.id}
                      className="absolute inset-1 p-2 rounded text-xs text-white cursor-pointer hover:opacity-80 group"
                      style={{ backgroundColor: cls.color }}
                      onClick={() => handleViewSubject(cls)}
                    >
                      <div className="font-medium truncate">{cls.name}</div>
                      <div className="opacity-80 truncate">{cls.classroom}</div>
                      <div className="opacity-80 truncate">{cls.professor}</div>
                      
                      {/* Action buttons on hover */}
                      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSubject(cls);
                          }}
                          className="p-1 rounded bg-black bg-opacity-20 hover:bg-opacity-40"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSubject(cls.id);
                          }}
                          className="p-1 rounded bg-black bg-opacity-20 hover:bg-opacity-40"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Subjects Overview */}
      <div 
        className="p-6 rounded-xl border"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
          Materias ({subjects.length})
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subjects.map((subject) => {
            const subjectTasks = getTasksForSubject(subject.id);
            const pendingTasks = subjectTasks.filter(t => t.status !== 'completed');
            
            return (
              <div
                key={subject.id}
                className="p-4 rounded-lg border cursor-pointer hover:opacity-80 group"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  borderLeft: `4px solid ${subject.color}`
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1" onClick={() => handleViewSubject(subject)}>
                    <h4 className="font-medium" style={{ color: colors.text }}>
                      {subject.name}
                    </h4>
                    <div className="flex items-center gap-2 mt-1 text-sm" style={{ color: colors.subtext0 }}>
                      <User className="w-3 h-3" />
                      {subject.professor}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-sm" style={{ color: colors.subtext0 }}>
                      <MapPin className="w-3 h-3" />
                      {subject.classroom}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleViewSubject(subject)}
                      className="p-1 rounded hover:opacity-80"
                      style={{ color: colors.blue }}
                      title="Ver detalles"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEditSubject(subject)}
                      className="p-1 rounded hover:opacity-80"
                      style={{ color: colors.subtext0 }}
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSubject(subject.id)}
                      className="p-1 rounded hover:opacity-80"
                      style={{ color: colors.red }}
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mb-2">
                  <div className="text-right">
                    <div 
                      className="text-lg font-bold"
                      style={{ color: subject.lastGrade >= 7 ? colors.green : colors.red }}
                    >
                      {subject.lastGrade}
                    </div>
                    <div className="text-xs" style={{ color: colors.subtext0 }}>
                      Última nota
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm font-medium" style={{ color: colors.text }}>
                      {subject.credits} créditos
                    </div>
                    <div className="text-xs" style={{ color: colors.subtext0 }}>
                      {subject.semester}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div style={{ color: colors.subtext0 }}>
                    {subject.schedule.length} clases/semana
                  </div>
                  <div 
                    className="px-2 py-1 rounded"
                    style={{ 
                      backgroundColor: pendingTasks.length > 0 ? colors.red + '20' : colors.green + '20',
                      color: pendingTasks.length > 0 ? colors.red : colors.green
                    }}
                  >
                    {pendingTasks.length} tareas pendientes
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Subject Modal */}
      {showSubjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-white p-6 rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: colors.mantle }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: colors.text }}>
                {editingSubject ? 'Editar Materia' : 'Nueva Materia'}
              </h3>
              <button
                onClick={resetSubjectForm}
                className="p-1 rounded hover:opacity-80"
                style={{ color: colors.subtext0 }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Nombre de la materia"
                  value={newSubject.name || ''}
                  onChange={(e) => setNewSubject(prev => ({ ...prev, name: e.target.value }))}
                  className="px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
                
                <input
                  type="text"
                  placeholder="Profesor"
                  value={newSubject.professor || ''}
                  onChange={(e) => setNewSubject(prev => ({ ...prev, professor: e.target.value }))}
                  className="px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
                
                <input
                  type="text"
                  placeholder="Aula"
                  value={newSubject.classroom || ''}
                  onChange={(e) => setNewSubject(prev => ({ ...prev, classroom: e.target.value }))}
                  className="px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
                
                <input
                  type="number"
                  placeholder="Créditos"
                  min="1"
                  max="10"
                  value={newSubject.credits || ''}
                  onChange={(e) => setNewSubject(prev => ({ ...prev, credits: parseInt(e.target.value) || 3 }))}
                  className="px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
                
                <input
                  type="number"
                  placeholder="Última nota"
                  min="0"
                  max="10"
                  step="0.1"
                  value={newSubject.lastGrade || ''}
                  onChange={(e) => setNewSubject(prev => ({ ...prev, lastGrade: parseFloat(e.target.value) || 0 }))}
                  className="px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
                
                <input
                  type="text"
                  placeholder="Semestre (ej: 2025-1)"
                  value={newSubject.semester || ''}
                  onChange={(e) => setNewSubject(prev => ({ ...prev, semester: e.target.value }))}
                  className="px-3 py-2 rounded border"
                  style={{
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    color: colors.text
                  }}
                />
              </div>
              
              <textarea
                placeholder="Descripción de la materia"
                value={newSubject.description || ''}
                onChange={(e) => setNewSubject(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 rounded border resize-none"
                rows={3}
                style={{
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              />
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Color
                </label>
                <div className="flex gap-2">
                  {availableColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewSubject(prev => ({ ...prev, color }))}
                      className={`w-8 h-8 rounded-full border-2 ${
                        newSubject.color === color ? 'ring-2 ring-offset-2' : ''
                      }`}
                      style={{ 
                        backgroundColor: color,
                        borderColor: colors.surface1,
                        ringColor: colors.blue
                      }}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium" style={{ color: colors.text }}>
                    Horarios de Clase
                  </label>
                  <button
                    onClick={addScheduleSlot}
                    className="px-3 py-1 rounded text-sm font-medium text-white"
                    style={{ backgroundColor: colors.green }}
                  >
                    <Plus className="w-3 h-3 inline mr-1" />
                    Agregar Horario
                  </button>
                </div>
                
                <div className="space-y-3">
                  {Array.isArray(tempSchedule) && tempSchedule.map((slot, index) => {
                    const isConflict = isTimeSlotOccupied(
                      slot.day, 
                      slot.startTime, 
                      slot.endTime, 
                      editingSubject?.id
                    );
                    
                    return (
                      <div 
                        key={index} 
                        className={`p-3 rounded border ${isConflict ? 'border-red-500' : ''}`}
                        style={{ 
                          backgroundColor: isConflict ? colors.red + '10' : colors.base,
                          borderColor: isConflict ? colors.red : colors.surface1
                        }}
                      >
                        <div className="grid grid-cols-4 gap-2 items-center">
                          <select
                            value={slot.day}
                            onChange={(e) => updateScheduleSlot(index, 'day', parseInt(e.target.value))}
                            className="px-2 py-1 rounded border text-sm"
                            style={{
                              backgroundColor: colors.mantle,
                              borderColor: colors.surface1,
                              color: colors.text
                            }}
                          >
                            {dayNames.map((day, dayIndex) => (
                              <option key={dayIndex} value={dayIndex}>
                                {day}
                              </option>
                            ))}
                          </select>
                          
                          <input
                            type="time"
                            value={slot.startTime}
                            onChange={(e) => updateScheduleSlot(index, 'startTime', e.target.value)}
                            className="px-2 py-1 rounded border text-sm"
                            style={{
                              backgroundColor: colors.mantle,
                              borderColor: colors.surface1,
                              color: colors.text
                            }}
                          />
                          
                          <input
                            type="time"
                            value={slot.endTime}
                            onChange={(e) => updateScheduleSlot(index, 'endTime', e.target.value)}
                            className="px-2 py-1 rounded border text-sm"
                            style={{
                              backgroundColor: colors.mantle,
                              borderColor: colors.surface1,
                              color: colors.text
                            }}
                          />
                          
                          <button
                            onClick={() => removeScheduleSlot(index)}
                            className="p-1 rounded text-white"
                            style={{ backgroundColor: colors.red }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                        
                        {isConflict && (
                          <div className="mt-2 text-xs" style={{ color: colors.red }}>
                            ⚠️ Conflicto de horario con otra materia
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mt-6">
              <button
                onClick={handleSaveSubject}
                disabled={Array.isArray(tempSchedule) && tempSchedule.some(slot => 
                  isTimeSlotOccupied(slot.day, slot.startTime, slot.endTime, editingSubject?.id)
                )}
                className="flex-1 py-2 rounded font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: colors.blue }}
              >
                <Save className="w-4 h-4 inline mr-2" />
                {editingSubject ? 'Actualizar' : 'Guardar'}
              </button>
              <button
                onClick={resetSubjectForm}
                className="flex-1 py-2 rounded border"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subject Details Modal */}
      {showSubjectDetails && selectedSubject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-white p-6 rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: colors.mantle }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: selectedSubject.color }}
                />
                <h3 className="text-xl font-semibold" style={{ color: colors.text }}>
                  {selectedSubject.name}
                </h3>
              </div>
              <button
                onClick={() => setShowSubjectDetails(false)}
                className="p-1 rounded hover:opacity-80"
                style={{ color: colors.subtext0 }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                    Profesor
                  </label>
                  <p style={{ color: colors.text }}>{selectedSubject.professor}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                    Aula
                  </label>
                  <p style={{ color: colors.text }}>{selectedSubject.classroom}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                    Créditos
                  </label>
                  <p style={{ color: colors.text }}>{selectedSubject.credits}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                    Semestre
                  </label>
                  <p style={{ color: colors.text }}>{selectedSubject.semester}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                    Última Nota
                  </label>
                  <p 
                    className="text-lg font-bold"
                    style={{ color: selectedSubject.lastGrade >= 7 ? colors.green : colors.red }}
                  >
                    {selectedSubject.lastGrade}
                  </p>
                </div>
              </div>
              
              {/* Description */}
              {selectedSubject.description && (
                <div>
                  <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                    Descripción
                  </label>
                  <p style={{ color: colors.text }}>{selectedSubject.description}</p>
                </div>
              )}
              
              {/* Schedule */}
              <div>
                <label className="text-sm font-medium mb-3 block" style={{ color: colors.subtext0 }}>
                  Horarios de Clase
                </label>
                <div className="space-y-2">
                  {(Array.isArray(selectedSubject.schedule) ? selectedSubject.schedule : []).map((schedule, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-4 p-3 rounded"
                      style={{ backgroundColor: colors.base }}
                    >
                      <span style={{ color: colors.text }}>
                        {dayNames[schedule.day]}
                      </span>
                      <span style={{ color: colors.subtext0 }}>
                        {schedule.startTime} - {schedule.endTime}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Tasks */}
              <div>
                <label className="text-sm font-medium mb-3 block" style={{ color: colors.subtext0 }}>
                  Tareas Asociadas ({getTasksForSubject(selectedSubject.id).length})
                </label>
                <div className="space-y-3">
                  {getTasksForSubject(selectedSubject.id).map((task) => (
                    <div 
                      key={task.id}
                      className="p-3 rounded border"
                      style={{ 
                        backgroundColor: colors.base,
                        borderColor: colors.surface1
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium" style={{ color: colors.text }}>
                          {task.title}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-xs px-2 py-1 rounded"
                            style={{ 
                              backgroundColor: getPriorityColor(task.priority) + '20',
                              color: getPriorityColor(task.priority)
                            }}
                          >
                            {task.priority}
                          </span>
                          <span 
                            className="text-xs px-2 py-1 rounded"
                            style={{ 
                              backgroundColor: getStatusColor(task.status) + '20',
                              color: getStatusColor(task.status)
                            }}
                          >
                            {task.status}
                          </span>
                        </div>
                      </div>
                      
                      {task.description && (
                        <p className="text-sm mb-2" style={{ color: colors.subtext0 }}>
                          {task.description}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs" style={{ color: colors.subtext0 }}>
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="w-3 h-3" />
                          {new Date(task.dueDate).toLocaleDateString()}
                        </div>
                        {task.deliveryLocation && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {task.deliveryLocation}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {getTasksForSubject(selectedSubject.id).length === 0 && (
                    <p className="text-center py-4" style={{ color: colors.subtext0 }}>
                      No hay tareas asociadas a esta materia
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mt-6">
              <button
                onClick={() => {
                  setShowSubjectDetails(false);
                  handleEditSubject(selectedSubject);
                }}
                className="flex-1 py-2 rounded font-medium text-white"
                style={{ backgroundColor: colors.blue }}
              >
                <Edit className="w-4 h-4 inline mr-2" />
                Editar Materia
              </button>
              <button
                onClick={() => setShowSubjectDetails(false)}
                className="flex-1 py-2 rounded border"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-white p-6 rounded-xl max-w-md w-full mx-4"
            style={{ backgroundColor: colors.mantle }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
              Nueva Tarea
            </h3>
            
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Título de la tarea"
                value={newTask.title || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 rounded border"
                style={{
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              />
              
              <textarea
                placeholder="Descripción"
                value={newTask.description || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 rounded border resize-none"
                rows={3}
                style={{
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              />
              
              <select
                value={newTask.subjectId || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, subjectId: e.target.value }))}
                className="w-full px-3 py-2 rounded border"
                style={{
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              >
                <option value="">Seleccionar materia</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
              
              <input
                type="date"
                value={newTask.dueDate || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                className="w-full px-3 py-2 rounded border"
                style={{
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              />
              
              <input
                type="text"
                placeholder="Lugar de entrega"
                value={newTask.deliveryLocation || ''}
                onChange={(e) => setNewTask(prev => ({ ...prev, deliveryLocation: e.target.value }))}
                className="w-full px-3 py-2 rounded border"
                style={{
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              />
              
              <select
                value={newTask.priority || 'medium'}
                onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value as any }))}
                className="w-full px-3 py-2 rounded border"
                style={{
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              >
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2 mt-6">
              <button
                onClick={handleSaveTask}
                className="flex-1 py-2 rounded font-medium text-white"
                style={{ backgroundColor: colors.green }}
              >
                Guardar
              </button>
              <button
                onClick={() => setShowTaskModal(false)}
                className="flex-1 py-2 rounded border"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicCalendar;